/**
 * 업체 신규 등록.
 * Figma 3장 카드(기본 정보 / 정산·초기 계약 / 첨부 서류)를 한 화면에서 저장한다.
 */
import {
  partnerCompanyService,
  type PartnerCompanyDocumentType,
} from '@/entities/partner-company/api/partner-company.api';
import { useCreatePartnerCompany } from '@/entities/partner-company/hooks/usePartnerCompanies';
import { FormDateField } from '@/shared/components/FormDateField';
import { FormSelectField } from '@/shared/components/FormSelectField';
import { FormTextField } from '@/shared/components/FormTextField';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import { Input } from '@/shared/components/ui/input';
import { FooterRow } from '@/shared/components/ui/row';
import {
  ROUTE_PATHS,
  partnerCompanyDetailPath,
} from '@/shared/constants/routes';
import {
  BUSINESS_REGISTRATION_NUMBER_MAX_LENGTH,
  BUSINESS_REGISTRATION_NUMBER_PLACEHOLDER,
  formatBusinessRegistrationNumber,
  isValidBusinessRegistrationNumber,
} from '@/shared/utils/businessRegistrationNumber';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Briefcase } from 'lucide-react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  PARTNER_COMPANY_DOCUMENT_SLOTS,
  PartnerCompanyDocumentSlotRow,
  SETTLEMENT_CYCLE_OPTIONS,
  getSettlementCycleLabel,
  resolvePartnerCompanyType,
  type PartnerCompanyDocumentSlotType,
} from './partnerCompanyForm';

const schema = z.object({
  companyName: z.string().trim().min(1, '업체명을 입력해주세요'),
  businessRegistrationNumber: z
    .string()
    .trim()
    .min(1, '사업자등록번호를 입력해주세요')
    .refine(
      isValidBusinessRegistrationNumber,
      '사업자등록번호는 000-00-00000 형식으로 입력해주세요',
    ),
  representativeName: z.string().min(1, '대표자명을 입력해주세요'),
  csoReportNumber: z
    .string()
    .trim()
    .min(1, '판촉영업자 신고번호를 입력해주세요'),
  contactNumber: z.string(),
  email: z.union([
    z.literal(''),
    z.string().email('이메일 형식이 올바르지 않습니다'),
  ]),
  address: z.string(),
  bankName: z.string(),
  accountNumber: z.string(),
  accountHolder: z.string(),
  settlementCycle: z.string(),
  contractStartDate: z.string().min(1, '계약 시작일을 선택해주세요'),
  contractEndDate: z.string(),
  effectiveStartDate: z.string().min(1, '적용 시작일을 선택해주세요'),
});

type FormValues = z.infer<typeof schema>;

const optionalText = (value: string) => value.trim() || undefined;

const uploadPartnerCompanyDocument = async (
  partnerCompanyId: number,
  documentType: PartnerCompanyDocumentType,
  file: File,
) => {
  const uploadInfo = await partnerCompanyService.getDocumentUploadUrl(
    file.name,
  );
  const uploadResponse = await fetch(uploadInfo.presignedUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  });
  if (!uploadResponse.ok) {
    throw new Error('파일 업로드에 실패했습니다.');
  }
  await partnerCompanyService.createDocument(partnerCompanyId, {
    documentType,
    originalFileName: file.name,
    s3FileKey: uploadInfo.uploadKey,
  });
};

export const PartnerCompanyCreatePage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const createPartnerCompany = useCreatePartnerCompany();
  const [documentFiles, setDocumentFiles] = useState<
    Partial<Record<PartnerCompanyDocumentSlotType, File>>
  >({});
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: '',
      businessRegistrationNumber: '',
      representativeName: '',
      csoReportNumber: '',
      contactNumber: '',
      email: '',
      address: '',
      bankName: '',
      accountNumber: '',
      accountHolder: '',
      settlementCycle: 'MONTHLY',
      contractStartDate: '',
      contractEndDate: '',
      effectiveStartDate: '',
    },
  });

  const goList = () => {
    navigate({ to: ROUTE_PATHS.PARTNER_COMPANY } as never);
  };

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true);
    try {
      const id = await createPartnerCompany.mutateAsync({
        companyName: values.companyName,
        // 업체명이 루다링크일 때만 RUDALINK, 나머지는 EXTERNAL
        companyType: resolvePartnerCompanyType(values.companyName),
        businessRegistrationNumber: values.businessRegistrationNumber,
        representativeName: values.representativeName,
        csoReportNumber: values.csoReportNumber,
        contactNumber: optionalText(values.contactNumber),
        email: optionalText(values.email),
        address: optionalText(values.address),
        bankName: optionalText(values.bankName),
        accountNumber: optionalText(values.accountNumber),
        accountHolder: optionalText(values.accountHolder),
      });

      try {
        await partnerCompanyService.createContract(id, {
          contractNumber: `C-${values.businessRegistrationNumber.replace(/-/g, '')}`,
          contractType: 'DEFAULT',
          startDate: values.contractStartDate,
          endDate: values.contractEndDate || values.contractStartDate,
          settlementTerms: getSettlementCycleLabel(values.settlementCycle),
        });
      } catch (contractError) {
        handleApiError(contractError, toast);
      }

      for (const slot of PARTNER_COMPANY_DOCUMENT_SLOTS) {
        const file = documentFiles[slot.type];
        if (!file) continue;
        try {
          await uploadPartnerCompanyDocument(id, slot.type, file);
        } catch (uploadError) {
          handleApiError(uploadError, toast);
        }
      }

      toast.success('업체를 등록했습니다.');
      navigate({ to: partnerCompanyDetailPath(id) } as never);
    } catch (error) {
      handleApiError(error, toast);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Briefcase}
        title="업체 등록"
        description="업체 기본 정보를 등록합니다"
      />

      <Form {...form}>
        <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
          <Card>
            <CardHeader>
              <CardTitle>업체 기본 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <FormTextField
                  control={form.control}
                  name="companyName"
                  label="업체명"
                  required
                  placeholder="업체명"
                />
                <FormField
                  control={form.control}
                  name="businessRegistrationNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>사업자등록번호</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={BUSINESS_REGISTRATION_NUMBER_MAX_LENGTH}
                          placeholder={BUSINESS_REGISTRATION_NUMBER_PLACEHOLDER}
                          onChange={(event) =>
                            field.onChange(
                              formatBusinessRegistrationNumber(
                                event.target.value,
                              ),
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormTextField
                  control={form.control}
                  name="representativeName"
                  label="대표자"
                  required
                  placeholder="대표자"
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <FormTextField
                  control={form.control}
                  name="csoReportNumber"
                  label="판촉영업자 신고번호"
                  required
                  placeholder="신고번호"
                />
                <FormTextField
                  control={form.control}
                  name="contactNumber"
                  label="대표 연락처"
                  placeholder="02-0000-0000"
                />
                <FormTextField
                  control={form.control}
                  name="email"
                  label="이메일"
                  placeholder="contact@company.test"
                />
                <FormTextField
                  control={form.control}
                  name="address"
                  label="주소"
                  placeholder="주소"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>정산 · 초기 계약 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <FormTextField
                  control={form.control}
                  name="bankName"
                  label="정산 은행"
                  placeholder="은행명"
                />
                <FormTextField
                  control={form.control}
                  name="accountNumber"
                  label="정산 계좌번호"
                  placeholder="계좌번호"
                />
                <FormTextField
                  control={form.control}
                  name="accountHolder"
                  label="예금주"
                  placeholder="예금주"
                />
                <FormSelectField
                  control={form.control}
                  name="settlementCycle"
                  label="정산 주기"
                  options={[...SETTLEMENT_CYCLE_OPTIONS]}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <FormDateField
                  control={form.control}
                  name="contractStartDate"
                  label="계약 시작일"
                  required
                  placeholder="날짜 선택"
                />
                <FormDateField
                  control={form.control}
                  name="contractEndDate"
                  label="계약 종료일"
                  placeholder="날짜 선택"
                />
                <FormDateField
                  control={form.control}
                  name="effectiveStartDate"
                  label="적용 시작일"
                  required
                  placeholder="날짜 선택"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>첨부 서류</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {PARTNER_COMPANY_DOCUMENT_SLOTS.map((slot) => (
                <PartnerCompanyDocumentSlotRow
                  key={slot.type}
                  label={slot.label}
                  fileName={documentFiles[slot.type]?.name}
                  onFileSelected={(file) =>
                    setDocumentFiles((current) => ({
                      ...current,
                      [slot.type]: file,
                    }))
                  }
                />
              ))}
            </CardContent>
          </Card>

          <FooterRow>
            <Button type="button" variant="outline" onClick={goList}>
              취소
            </Button>
            <MenuWriteGate>
              <Button
                type="submit"
                className="ruda-button"
                disabled={createPartnerCompany.isPending || submitting}
              >
                업체 등록
              </Button>
            </MenuWriteGate>
          </FooterRow>
        </form>
      </Form>
    </div>
  );
};
